import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Brain, Send, Loader2, Share2, Copy, Mail, MessageSquare, Check, X, Clock, History, Trash2,
  ThumbsUp, ThumbsDown, MessageSquarePlus, CheckCircle2, CornerDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDisplayDateTime } from '@/utils/dateFormat';
import ReactMarkdown from 'react-markdown';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';

function ShareMenu({ content, query }) {
  const [copied, setCopied] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [slackOpen, setSlackOpen] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`KEYA PULSE AI Insight\n\nQuery: ${query}\n\n${content}`);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="share-insight-btn" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-150 mt-2 px-2 py-1 rounded hover:bg-muted/50">
            <Share2 size={12} /> Share
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="start">
          <button onClick={handleCopy} data-testid="share-copy-btn" className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded hover:bg-muted/50 transition-colors duration-150">
            {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />} Copy to clipboard
          </button>
          <button onClick={() => setEmailOpen(true)} data-testid="share-email-btn" className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded hover:bg-muted/50 transition-colors duration-150">
            <Mail size={14} /> Share via Email
          </button>
          <button onClick={() => setSlackOpen(true)} data-testid="share-slack-btn" className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded hover:bg-muted/50 transition-colors duration-150">
            <MessageSquare size={14} /> Share to Slack
          </button>
        </PopoverContent>
      </Popover>

      <EmailShareDialog open={emailOpen} onClose={() => setEmailOpen(false)} content={content} query={query} />
      <SlackShareDialog open={slackOpen} onClose={() => setSlackOpen(false)} content={content} query={query} />
    </>
  );
}

function EmailShareDialog({ open, onClose, content, query }) {
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = () => {
    const subject = encodeURIComponent(`KEYA PULSE Insight: ${query?.slice(0, 60) || 'Sales Analysis'}`);
    const body = encodeURIComponent(`Hi,\n\nHere's an AI-generated insight from KEYA PULSE:\n\nQuery: ${query}\n\n${content}\n\n---\nGenerated by KEYA PULSE v1.0 AI Analytics`);

    if (to) {
      window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_blank');
    } else {
      window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    }
    toast.success('Email client opened');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="email-share-dialog" onInteractOutside={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">Share via Email</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Recipient (optional)</Label>
            <Input placeholder="colleague@company.com" value={to} onChange={e => setTo(e.target.value)} data-testid="email-recipient-input" />
          </div>
          <div className="bg-muted/50 rounded-md p-3 max-h-40 overflow-auto">
            <p className="text-xs text-muted-foreground font-medium mb-1">Preview:</p>
            <p className="text-xs text-foreground whitespace-pre-wrap">{content?.slice(0, 300)}{content?.length > 300 ? '...' : ''}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSend} data-testid="email-send-btn">
            <Mail size={14} className="mr-1.5" /> Open Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SlackShareDialog({ open, onClose, content, query }) {
  const [webhookUrl, setWebhookUrl] = useState(localStorage.getItem('keya_slack_webhook') || '');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!webhookUrl.trim()) {
      toast.error('Please enter a Slack webhook URL');
      return;
    }
    setSending(true);
    try {
      localStorage.setItem('keya_slack_webhook', webhookUrl);
      const message = {
        text: `*KEYA PULSE AI Insight*\n\n*Query:* ${query}\n\n${content}\n\n_Generated by KEYA PULSE v1.0_`
      };
      await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(message),
        mode: 'no-cors'
      });
      toast.success('Sent to Slack');
      onClose();
    } catch (err) {
      toast.error('Failed to send to Slack');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="slack-share-dialog" onInteractOutside={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">Share to Slack</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Slack Webhook URL</Label>
            <Input placeholder="https://hooks.slack.com/services/..." value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} data-testid="slack-webhook-input" />
            <p className="text-[11px] text-muted-foreground">Create a webhook at Slack App Settings &gt; Incoming Webhooks. URL is saved locally for reuse.</p>
          </div>
          <div className="bg-muted/50 rounded-md p-3 max-h-40 overflow-auto">
            <p className="text-xs text-muted-foreground font-medium mb-1">Message preview:</p>
            <p className="text-xs text-foreground whitespace-pre-wrap">{content?.slice(0, 300)}{content?.length > 300 ? '...' : ''}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSend} disabled={sending} data-testid="slack-send-btn">
            {sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <MessageSquare size={14} className="mr-1.5" />}
            Send to Slack
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_SUGGESTIONS = [
  'How did each zone do last month compared with the month before?',
  'Which outlets stopped ordering from us?',
  'Which zone has the lowest visit conversion rate?',
  'Why aren\'t outlets ordering?',
];

// A streaming answer ends with a 'Follow-ups:' line that becomes buttons once it's done;
// hide the raw line while it streams (the server strips it from the finished answer).
const stripFollowups = (t) => (t || '').replace(/\n*[^\S\n]*\W*follow[- ]?ups?\W*:[^\n]*\s*$/i, '');

export default function AIInsightsPage() {
  const { api, token } = useAuth();
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [turnsLeft, setTurnsLeft] = useState(null);
  const [viewNote, setViewNote] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [tab, setTab] = useState('chat'); // 'chat' or 'history'
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const endRef = useRef(null);
  const abortRef = useRef(null);
  const busyRef = useRef(false);     // state updates lag a render; this stops a double send

  useEffect(() => {
    api.get('/ai/suggestions')
      .then(res => setSuggestions(res.data?.suggestions?.length ? res.data.suggestions : FALLBACK_SUGGESTIONS))
      .catch(() => setSuggestions(FALLBACK_SUGGESTIONS));
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const loadHistory = async (archived = showArchived) => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/ai/history', { params: { include_archived: archived, limit: 50 } });
      setHistory(res.data);
    } catch (err) { console.error(err); }
    finally { setHistoryLoading(false); }
  };

  const update = (idx, fn) => setMessages(prev => {
    const next = [...prev];
    next[idx] = fn({ ...next[idx] });
    return next;
  });

  const onEvent = (idx, ev) => {
    if (ev.type === 'meta') setConversationId(ev.conversation_id);
    if (ev.type === 'done') setTurnsLeft(ev.turns_left);
    update(idx, m => {
      switch (ev.type) {
        case 'text': m.text = (m.text || '') + ev.delta; break;
        case 'tool_start': m.text = ''; break;          // text before a lookup was only a preamble
        case 'status': m.status = [...(m.status || []), ev.text]; break;
        case 'replace': m.text = ev.text; break;
        case 'done':
          Object.assign(m, { text: ev.answer, followups: ev.followups || [], sources: ev.sources || [],
            insightId: ev.insight_id, cached: ev.cached, cachedAt: ev.cached_at, streaming: false });
          break;
        case 'error': Object.assign(m, { error: ev.message, errorCode: ev.code, streaming: false }); break;
        default: break;
      }
      return m;
    });
  };

  const ask = async (text) => {
    const question = (text ?? query).trim();
    if (!question || busyRef.current) return;
    busyRef.current = true;
    setQuery('');
    setTab('chat');
    setViewNote('');
    setBusy(true);
    const idx = messages.length + 1;     // ask() is locked by busy, so messages is current
    setMessages([...messages, { role: 'user', content: question },
      { role: 'assistant', text: '', status: [], streaming: true, query: question }]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: question, conversation_id: conversationId }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(res.status === 401 ? 'Your session has expired. Please log in again.'
          : (body.detail || 'Could not get an answer. Please try again.'));
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let cut;
        while ((cut = buf.indexOf('\n\n')) >= 0) {
          const chunk = buf.slice(0, cut);
          buf = buf.slice(cut + 2);
          for (const line of chunk.split('\n')) {
            if (line.startsWith('data: ')) onEvent(idx, JSON.parse(line.slice(6)));
          }
        }
      }
      update(idx, m => (m.streaming ? { ...m, streaming: false, error: m.text ? '' : 'The answer stopped unexpectedly. Please try again.' } : m));
    } catch (err) {
      if (err.name !== 'AbortError') {
        update(idx, m => ({ ...m, streaming: false, error: err.message || 'Could not get an answer.' }));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const newConversation = () => {
    abortRef.current?.abort();
    setMessages([]);
    setConversationId(null);
    setTurnsLeft(null);
    setViewNote('');
  };

  const rate = async (idx, insightId, current, rating) => {
    const next = current === rating ? 0 : rating;
    update(idx, m => ({ ...m, rating: next }));
    try {
      await api.post('/ai/feedback', { insight_id: insightId, rating: next });
      if (next === -1) toast.info('Thanks - this answer won\'t be reused, and it helps us improve.');
    } catch { toast.error('Could not save your feedback'); }
  };

  const openFromHistory = async (item) => {
    if (!item.conversation_id) {           // saved before conversations existed
      setMessages([{ role: 'user', content: item.query },
        { role: 'assistant', text: item.response, query: item.query, insightId: item.id, rating: item.rating }]);
      setConversationId(null);
      setViewNote('A saved answer. Your next question starts a new conversation.');
      setTab('chat');
      return;
    }
    try {
      const res = await api.get(`/ai/conversations/${item.conversation_id}`);
      const msgs = [];
      res.data.turns.forEach(t => {
        msgs.push({ role: 'user', content: t.query });
        msgs.push({ role: 'assistant', text: t.answer, query: t.query, insightId: t.insight_id, rating: t.rating,
          followups: t.followups, sources: t.sources });
      });
      setMessages(msgs);
      setConversationId(res.data.can_continue ? res.data.conversation_id : null);
      setViewNote(res.data.can_continue ? 'Saved conversation - ask a follow-up to continue it.'
        : `Saved conversation by ${res.data.by || 'a colleague'}. Your next question starts a new one.`);
      setTab('chat');
    } catch { toast.error('Could not open that conversation'); }
  };

  const deleteInsight = async (id) => {
    try {
      await api.delete(`/ai/history/${id}`);
      toast.success('Insight deleted');
      loadHistory();
    } catch { toast.error('Failed to delete'); }
  };

  const formatDate = (iso) => formatDisplayDateTime(iso);
  const lastAssistant = messages.length - 1;
  const full = messages[lastAssistant]?.errorCode === 'conversation_full' || turnsLeft === 0;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="ai-insights-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">AI Insights</h1>
          <p className="text-sm text-muted-foreground mt-1">Ask about sales, visits, outlets and FEs in plain words</p>
        </div>
        <div className="flex items-center gap-2">
          {tab === 'chat' && messages.length > 0 && (
            <Button variant="outline" size="sm" onClick={newConversation} disabled={busy} data-testid="ai-new-conversation">
              <MessageSquarePlus size={14} className="mr-1.5" /> New conversation
            </Button>
          )}
          <div className="flex gap-1 bg-muted/50 rounded-md p-0.5">
            <button onClick={() => setTab('chat')} data-testid="tab-chat"
              className={`px-3 py-1.5 text-xs font-heading font-medium rounded transition-colors duration-150 ${tab === 'chat' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              Chat
            </button>
            <button onClick={() => { setTab('history'); loadHistory(); }} data-testid="tab-history"
              className={`px-3 py-1.5 text-xs font-heading font-medium rounded transition-colors duration-150 ${tab === 'history' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              History
            </button>
          </div>
        </div>
      </div>

      {tab === 'chat' ? (
        <>
          {messages.length === 0 && (
            <div className="bg-card border border-border rounded-md p-8 text-center">
              <Brain size={40} strokeWidth={1} className="mx-auto text-primary mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">Ask anything about your sales data</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-xl mx-auto">
                Type the way you'd ask a colleague, in English or Hinglish. Names don't need to be exact.
                Every answer says which period and data it's based on.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
                {suggestions.map((q, i) => (
                  <button key={i} onClick={() => ask(q)} data-testid={`suggested-query-${i}`}
                    className="text-left text-sm p-3 rounded-md border border-border hover:bg-muted/50 transition-colors duration-150 text-muted-foreground hover:text-foreground">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {viewNote && (
            <div className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-md px-3 py-2" data-testid="ai-view-note">
              {viewNote}
            </div>
          )}

          <div className="space-y-4">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'user' ? (
                  <div className="max-w-[85%] rounded-md p-4 text-sm leading-relaxed bg-primary text-primary-foreground" data-testid={`ai-message-${i}`}>
                    <p className="whitespace-pre-wrap font-body">{msg.content}</p>
                  </div>
                ) : (
                  <div className="max-w-[85%] w-full rounded-md p-4 text-sm leading-relaxed bg-card border border-border text-foreground" data-testid={`ai-message-${i}`}>
                    {msg.cached && (
                      <div className="flex items-center gap-1.5 mb-2 text-[11px] text-amber-600 dark:text-amber-400">
                        <Clock size={11} /> Answered earlier today ({formatDate(msg.cachedAt)}) from the same data
                      </div>
                    )}
                    {msg.streaming && (
                      <ul className="mb-2 space-y-1" data-testid={`ai-status-${i}`}>
                        {(msg.status || []).map((s, j) => (
                          <li key={j} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            {j === msg.status.length - 1 && !msg.text
                              ? <Loader2 size={11} className="animate-spin text-primary" />
                              : <CheckCircle2 size={11} className="text-green-600" />}
                            {s}
                          </li>
                        ))}
                        {!msg.text && !(msg.status || []).length && (
                          <li className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <Loader2 size={11} className="animate-spin text-primary" /> Reading your question
                          </li>
                        )}
                      </ul>
                    )}
                    {msg.error ? (
                      <div className="space-y-2">
                        <p className="text-destructive text-sm">{msg.error}</p>
                        {msg.errorCode === 'conversation_full' && (
                          <Button size="sm" variant="outline" onClick={newConversation}>
                            <MessageSquarePlus size={14} className="mr-1.5" /> Start a new conversation
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="font-body text-sm leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0
                        [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5
                        [&_li]:my-0.5 [&_li]:pl-0.5 [&_strong]:font-semibold [&_strong]:text-foreground
                        [&_h2]:font-heading [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1.5
                        [&_h3]:font-heading [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1
                        [&_a]:text-primary [&_a]:underline [&_code]:text-xs">
                        <ReactMarkdown>{msg.streaming ? stripFollowups(msg.text) : msg.text}</ReactMarkdown>
                      </div>
                    )}
                    {!msg.streaming && !msg.error && (
                      <>
                        {(msg.sources || []).length > 0 && (
                          <p className="text-[11px] text-muted-foreground mt-3 pt-2 border-t border-border" data-testid={`ai-sources-${i}`}>
                            Based on: {msg.sources.join(' ; ')}
                          </p>
                        )}
                        <div className="flex items-center gap-1 mt-2">
                          {msg.insightId && (
                            <>
                              <button onClick={() => rate(i, msg.insightId, msg.rating, 1)} title="Helpful" data-testid={`ai-rate-up-${i}`}
                                className={`p-1.5 rounded hover:bg-muted/50 ${msg.rating === 1 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                <ThumbsUp size={13} />
                              </button>
                              <button onClick={() => rate(i, msg.insightId, msg.rating, -1)} title="Not helpful or wrong" data-testid={`ai-rate-down-${i}`}
                                className={`p-1.5 rounded hover:bg-muted/50 ${msg.rating === -1 ? 'text-destructive' : 'text-muted-foreground'}`}>
                                <ThumbsDown size={13} />
                              </button>
                            </>
                          )}
                          <ShareMenu content={msg.text} query={msg.query} />
                        </div>
                        {i === lastAssistant && !busy && !full && (msg.followups || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3" data-testid="ai-followups">
                            {msg.followups.map((f, j) => (
                              <button key={j} onClick={() => ask(f)}
                                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-150">
                                <CornerDownRight size={11} /> {f}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="sticky bottom-0 bg-background pt-4 pb-2">
            {full ? (
              <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground border border-border rounded-md p-3">
                This conversation is full.
                <Button size="sm" onClick={newConversation}><MessageSquarePlus size={14} className="mr-1.5" /> New conversation</Button>
              </div>
            ) : (
              <form onSubmit={e => { e.preventDefault(); ask(); }} className="flex gap-2" data-testid="ai-query-form">
                <Input placeholder={messages.length ? 'Ask a follow-up...' : 'e.g. How is Abdul Rehman doing this month?'}
                  value={query} onChange={e => setQuery(e.target.value)} disabled={busy}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); ask(); } }}
                  className="flex-1" data-testid="ai-query-input" />
                <Button type="submit" disabled={busy || !query.trim()} data-testid="ai-query-submit" size="icon">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </Button>
              </form>
            )}
          </div>
        </>
      ) : (
        /* History Tab */
        <div className="space-y-4" data-testid="ai-history-tab">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {history.total || 0} saved answers {history.archived_count > 0 && `(${history.archived_count} archived)`}
            </p>
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={e => { setShowArchived(e.target.checked); loadHistory(e.target.checked); }}
                className="rounded border-border" data-testid="show-archived-toggle" />
              Show archived
            </label>
          </div>

          {historyLoading ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Loading history...</div>
          ) : (history.insights || []).length === 0 ? (
            <div className="bg-card border border-border rounded-md p-8 text-center">
              <History size={32} strokeWidth={1} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No saved answers yet. Ask a question to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(history.insights || []).map((item, i) => (
                <div key={item.id || i} className={`bg-card border rounded-md p-4 animate-fade-in-up ${item.archived ? 'border-border/50 opacity-60' : 'border-border'}`}
                  style={{ animationDelay: `${i * 40}ms` }} data-testid={`history-item-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{item.query}</p>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock size={10} /> {formatDate(item.created_at)}</span>
                        <span>{item.created_by}</span>
                        {item.turn > 1 && <span className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Follow-up</span>}
                        {item.rating === 1 && <ThumbsUp size={11} className="text-green-600" />}
                        {item.rating === -1 && <ThumbsDown size={11} className="text-destructive" />}
                        {item.archived && <span className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Archived</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => openFromHistory(item)} data-testid={`history-view-${i}`}
                        className="text-xs text-primary hover:underline px-2 py-1">Open</button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`history-delete-${i}`}
                            className="text-xs text-muted-foreground hover:text-destructive px-2 py-1">
                            <Trash2 size={12} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="delete-confirm-dialog" onInteractOutside={e => e.preventDefault()}>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">Delete Insight</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this insight? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteInsight(item.id)} data-testid="delete-confirm-btn"
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{item.response?.slice(0, 200)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
